import { useState } from "react";
import type { CharacterState } from "@/data/types";
import { getRulesForVersion, rulesRegistry } from "@/data/rules";
import { checkUltimateEligibility } from "@/lib/ultimateWarlock";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Circle, Crown } from "lucide-react";

// Requirement checklist with a rules-version selector. With `onChange`, also
// offers the ascend/revoke toggle.
export function UltimateWarlockPanel({
  state,
  onChange,
}: {
  state: CharacterState;
  onChange?: (s: CharacterState) => void;
}) {
  const [version, setVersion] = useState(state.rulesVersion);
  const rules = getRulesForVersion(version);
  const { groups, metCount, totalCount, eligible } = checkUltimateEligibility(
    state,
    rules
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-muted-foreground">Validate against:</p>
        <div className="flex gap-1">
          {Object.entries(rulesRegistry).map(([v, r]) => (
            <Button
              key={v}
              size="sm"
              variant={v === version ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={() => setVersion(v)}
            >
              {r.metadata.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Requirements</p>
        <Badge variant={eligible ? "default" : "secondary"} className="text-xs">
          {metCount}/{totalCount}
        </Badge>
      </div>

      {groups.map((group) => {
        const groupMet = group.items.every((i) => i.met);
        return (
          <div key={group.title} className="space-y-1">
            <p
              className={`text-sm font-medium flex items-center gap-2 ${groupMet ? "text-muted-foreground" : ""}`}
            >
              {groupMet ? (
                <Check className="h-4 w-4 text-primary shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              {group.title}
              <span className="text-xs text-muted-foreground font-normal">
                {group.items.filter((i) => i.met).length}/{group.items.length}
              </span>
            </p>
            {!groupMet && (
              <ul className="space-y-1 text-sm pl-6">
                {/* Outstanding items first, completed ones crossed off */}
                {[...group.items]
                  .sort((a, b) => Number(a.met) - Number(b.met))
                  .map((item) => (
                    <li
                      key={item.label}
                      className={`flex items-center gap-2 ${item.met ? "text-muted-foreground line-through" : ""}`}
                    >
                      {item.met ? (
                        <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      )}
                      {item.label}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        );
      })}

      {state.ultimateWarlock && !eligible && (
        <p className="text-xs text-muted-foreground">
          Already ascended: Ultimate Warlock status is kept even if these
          requirements are no longer met.
        </p>
      )}

      {onChange &&
        (state.ultimateWarlock ? (
          <div className="flex items-center gap-2">
            <Badge className="gap-1">
              <Crown className="h-3 w-3" />
              Ultimate Warlock
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => onChange({ ...state, ultimateWarlock: undefined })}
            >
              Revoke status
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            disabled={!eligible}
            onClick={() => onChange({ ...state, ultimateWarlock: true })}
          >
            <Crown className="h-4 w-4 mr-1.5" />
            Ascend as Ultimate Warlock
          </Button>
        ))}
    </div>
  );
}

// Shown on the sheet while the character qualifies under its own rules but
// hasn't ascended yet.
export function UltimateEligibleBanner({
  state,
  onChange,
}: {
  state: CharacterState;
  onChange: (s: CharacterState) => void;
}) {
  const rules = getRulesForVersion(state.rulesVersion);
  if (state.ultimateWarlock || !checkUltimateEligibility(state, rules).eligible)
    return null;
  return (
    <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <Crown className="h-5 w-5 shrink-0 text-amber-500" />
      <p className="text-sm flex-1">
        <strong>Eligible to ascend!</strong> This Warlock meets every
        requirement and may now become an Ultimate Warlock.
      </p>
      <Button
        size="sm"
        onClick={() => onChange({ ...state, ultimateWarlock: true })}
      >
        Ascend
      </Button>
    </div>
  );
}
